import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, Files, Pause, Pencil, Play, Plus, Trash2, Workflow as WorkflowIcon } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import TableCard from '@/components/layout/TableCard'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import ActionMenu from '@/components/ui/ActionMenu'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import { useCreateWorkflow, useDeleteWorkflow, useUpdateWorkflow, useWorkflows } from '@/features/workflows/useWorkflows'
import { useWorkflowLookups } from '@/features/workflows/useWorkflowLookups'
import { delayOf, describeAction, describeTrigger } from '@/features/workflows/workflowMeta'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { cn } from '@/lib/cn'
import type { Workflow, WorkflowInput } from '@/api/workflows'

const inputOf = (w: Workflow, overrides: Partial<WorkflowInput> = {}): WorkflowInput => ({
  name: w.name,
  enabled: w.enabled,
  triggerType: w.triggerType,
  triggerConfig: w.triggerConfig,
  conditions: w.conditions,
  actions: w.actions,
  ...overrides,
})

/**
 * Workflows: the automations that run on their own. Each row reads as a sentence — when it runs and the
 * steps it takes — so you can tell them apart without opening them; each opens on its own page.
 */
export default function Workflows() {
  const navigate = useNavigate()
  const { data: workflows, isLoading } = useWorkflows()
  const { lookups } = useWorkflowLookups()
  const create = useCreateWorkflow()
  const update = useUpdateWorkflow()
  const del = useDeleteWorkflow()
  const [deleting, setDeleting] = useState<Workflow | null>(null)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<'all' | 'on' | 'off'>('all')
  const all = workflows ?? []
  const shown = all.filter(
    (w) =>
      (status === 'all' || (status === 'on') === w.enabled) &&
      matchesQuery(query, w.name, describeTrigger(w, lookups), ...w.actions.map((a) => describeAction(a, lookups))),
  )

  return (
    <PageContainer width="full">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-brand-darkest">Workflows</h1>
          <p className="text-sm text-gray-500">Automations that run on their own — when something happens, take these steps.</p>
        </div>
        <Link to="/workflows/new" className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
          <Plus size={15} /> New workflow
        </Link>
      </div>

      {all.length > 0 && (
        <IndexToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search workflows by name, trigger or step…"
          statuses={[
            { value: 'all', label: 'All', count: all.length },
            { value: 'on', label: 'On', count: all.filter((w) => w.enabled).length },
            { value: 'off', label: 'Off', count: all.filter((w) => !w.enabled).length },
          ]}
          status={status}
          onStatusChange={setStatus}
          shown={shown.length}
          total={all.length}
        />
      )}
      <TableCard>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 border-b border-gray-100 bg-gray-50/60">
              <th className="px-4 py-2.5">Workflow</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Runs when</th>
              <th className="px-3 py-2.5">Steps</th>
              <th className="px-3 py-2.5 w-28"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((w) => (
              <tr key={w.id} onClick={() => navigate(`/workflows/${w.id}`)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/80 cursor-pointer align-top">
                <td className="px-4 py-3 min-w-[12rem]">
                  <Link to={`/workflows/${w.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-brand-darkest hover:text-brand-primary">
                    {w.name}
                  </Link>
                </td>
                <td className="px-3 py-3">
                  <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full', w.enabled ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500')}>
                    <span className={cn('w-1.5 h-1.5 rounded-full', w.enabled ? 'bg-success' : 'bg-gray-400')} />
                    {w.enabled ? 'On' : 'Off'}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-700 min-w-[14rem]">
                  {describeTrigger(w, lookups)}
                  {w.conditions.length > 0 && <div className="text-xs text-gray-500">if {w.conditions.length} condition{w.conditions.length === 1 ? '' : 's'} match</div>}
                </td>
                <td className="px-3 py-3 min-w-[18rem]">
                  <ol className="space-y-0.5">
                    {w.actions.map((a, i) => (
                      <li key={i} className="text-gray-700">
                        <span className="text-gray-400 tabular-nums">{i + 1}. </span>
                        {delayOf(a) > 0 && <span className="text-gray-500">after {delayOf(a)}d — </span>}
                        {describeAction(a, lookups)}
                      </li>
                    ))}
                  </ol>
                </td>
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/workflows/${w.id}`} className="px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50">Edit</Link>
                    <ActionMenu
                      label={`Actions for ${w.name}`}
                      items={[
                        { label: 'Edit workflow', icon: <Pencil size={15} />, onClick: () => navigate(`/workflows/${w.id}`) },
                        w.enabled
                          ? { label: 'Turn off', icon: <Pause size={15} />, onClick: () => update.mutate({ id: w.id, input: inputOf(w, { enabled: false }) }) }
                          : { label: 'Turn on', icon: <Play size={15} />, onClick: () => update.mutate({ id: w.id, input: inputOf(w, { enabled: true }) }) },
                        {
                          label: 'Duplicate',
                          icon: <Files size={15} />,
                          onClick: () => create.mutate(inputOf(w, { name: `${w.name} (copy)`, enabled: false }), { onSuccess: (c) => navigate(`/workflows/${c.id}`) }),
                        },
                        { label: 'Delete workflow', icon: <Trash2 size={15} />, danger: true, onClick: () => setDeleting(w) },
                      ]}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {all.length > 0 && shown.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No workflows match.{' '}
            <button type="button" onClick={() => { setQuery(''); setStatus('all') }} className="font-medium text-brand-primary hover:underline">Clear the search</button>
          </div>
        )}
        {!isLoading && (workflows ?? []).length === 0 && (
          <div className="px-4 py-12 text-center">
            <WorkflowIcon size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm font-medium text-brand-darkest">No workflows yet</p>
            <p className="text-sm text-gray-500 mb-3">e.g. when a deal is sold, email the client a congratulations and remind the agent to send the pack.</p>
            <Link to="/workflows/new" className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
              <Plus size={15} /> Create a workflow
            </Link>
          </div>
        )}
      </TableCard>

      {deleting && (
        <Modal
          title="Delete workflow"
          tone="danger"
          icon={<AlertTriangle size={17} />}
          size="sm"
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button type="button" onClick={() => setDeleting(null)} className={modalButton.ghost}>Cancel</button>
              <button type="button" disabled={del.isPending} onClick={() => del.mutate(deleting.id, { onSuccess: () => setDeleting(null) })} className={modalButton.danger}>
                {del.isPending ? 'Deleting…' : 'Delete workflow'}
              </button>
            </>
          }
        >
          <p className="text-sm text-gray-600">
            Delete <span className="font-semibold text-brand-darkest">{deleting.name}</span>? It stops running straight away, including any
            steps still waiting. Messages and tasks it already created stay. To pause it instead, turn it off.
          </p>
          {del.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(del.error)}</p>}
        </Modal>
      )}
    </PageContainer>
  )
}
